import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../main/initializers/axios';
import { ApiConstants } from '../../main/constants/ApiConstants';
import './CompanyEmailNotification.css';

interface CompanyEmailNotificationUser {
  userId: number;
  userName: string;
  fullName: string;
  email: string;
  isSelected: boolean;
}

interface CompanyEmailNotificationConfig {
  emailType: number;
  emailTypeName: string;
  users: CompanyEmailNotificationUser[];
}

const CompanyEmailNotification: React.FC = () => {
  const { i18n } = useTranslation();
  const [emailNotifications, setEmailNotifications] = useState<CompanyEmailNotificationConfig[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerms, setSearchTerms] = useState<Record<number, string>>({});

  const getEmailTypeLabel = (config: CompanyEmailNotificationConfig) => {
    switch (config.emailType) {
      case 1:
        return i18n.t('company.emailTypeReservationRequest', { defaultValue: 'Reservation request' });
      case 2:
        return i18n.t('company.emailTypeApproval', { defaultValue: 'Approval' });
      case 3:
        return i18n.t('company.emailTypeCancellation', { defaultValue: 'Cancellation' });
      case 4:
        return i18n.t('company.emailTypeLowStock', { defaultValue: 'Low stock' });
      case 5:
        return i18n.t('company.emailTypeServerHealth', { defaultValue: 'Server health' });
      case 6:
        return i18n.t('company.emailTypeCardPayment', { defaultValue: 'Card payment' });
      default:
        return config.emailTypeName;
    }
  };

  const fetchEmailNotifications = () => {
    setIsLoading(true);

    axiosInstance
      .get<CompanyEmailNotificationConfig[]>(ApiConstants.companyEmailNotification.list)
      .then((res) => setEmailNotifications(res.data || []))
      .catch((err) => {
        console.error(err);
        setEmailNotifications([]);
        toast.error(i18n.t('company.emailNotificationsLoadFailed', { defaultValue: 'Failed to load email notifications.' }));
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchEmailNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchTerms((prev) => {
      const next: Record<number, string> = {};
      emailNotifications.forEach((config) => {
        next[config.emailType] = prev[config.emailType] ?? '';
      });
      return next;
    });
  }, [emailNotifications]);

  const toggleEmailNotificationUser = (emailType: number, userId: number) => {
    setEmailNotifications((prev) =>
      prev.map((config) =>
        config.emailType !== emailType
          ? config
          : {
              ...config,
              users: config.users.map((user) =>
                user.userId !== userId
                  ? user
                  : {
                      ...user,
                      isSelected: !user.isSelected,
                    }
              ),
            }
      )
    );
  };

  const setAllUsersSelection = (emailType: number, isSelected: boolean) => {
    setEmailNotifications((prev) =>
      prev.map((config) =>
        config.emailType !== emailType
          ? config
          : {
              ...config,
              users: config.users.map((user) => ({
                ...user,
                isSelected,
              })),
            }
      )
    );
  };

  const updateSearchTerm = (emailType: number, value: string) => {
    setSearchTerms((prev) => ({
      ...prev,
      [emailType]: value,
    }));
  };

  const totalSelectedUsers = useMemo(
    () => emailNotifications.reduce((sum, config) => sum + config.users.filter((user) => user.isSelected).length, 0),
    [emailNotifications]
  );

  const handleSave = async () => {
    try {
      setIsSaving(true);

      await Promise.all(
        emailNotifications.map((config) =>
          axiosInstance.post(ApiConstants.companyEmailNotification.save, {
            emailType: config.emailType,
            userIds: config.users.filter((user) => user.isSelected).map((user) => user.userId),
          })
        )
      );

      toast.success(i18n.t('company.emailNotificationsSaved', { defaultValue: 'Email notifications saved successfully.' }));
      fetchEmailNotifications();
    } catch (err) {
      console.error(err);
      toast.error(i18n.t('company.emailNotificationsSaveFailed', { defaultValue: 'Failed to save email notifications.' }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="companyEmailPage">
      <div className="companyEmailPageHeader">
        <div>
          <h1>{i18n.t('company.emailNotifications', { defaultValue: 'Email notifications' })}</h1>
          <p>
            {i18n.t('company.emailNotificationsDescription', {
              defaultValue: 'Choose which company users will be notified for each hotel email type.',
            })}
          </p>
        </div>
      </div>

      <div className="companyEmailPageBody">
        {isLoading ? (
          <div className="companyEmailPageEmpty">
            {i18n.t('generic.loading', { defaultValue: 'Loading...' })}
          </div>
        ) : emailNotifications.length === 0 ? (
          <div className="companyEmailPageEmpty">
            {i18n.t('company.noEmailNotificationTypes', { defaultValue: 'No email notification types available.' })}
          </div>
        ) : (
          <div className="companyEmailGrid">
            {emailNotifications.map((config) => {
              const selectedUsers = config.users.filter((user) => user.isSelected);
              const searchTerm = (searchTerms[config.emailType] ?? '').trim().toLowerCase();
              const filteredUsers = !searchTerm
                ? config.users
                : config.users.filter((user) =>
                    [user.fullName, user.userName, user.email]
                      .filter(Boolean)
                      .some((value) => value!.toLowerCase().includes(searchTerm))
                  );

              return (
                <div key={config.emailType} className="companyEmailCard">
                  <div className="companyEmailCardHeader">
                    <div>
                      <h3>{getEmailTypeLabel(config)}</h3>
                      <p>{i18n.t('company.emailNotificationsCardHint', { defaultValue: 'Pick one or more recipients for this email type.' })}</p>
                    </div>
                    <span>
                      {selectedUsers.length}
                      {` ${i18n.t('company.selectedUsers', { defaultValue: 'selected' })}`}
                    </span>
                  </div>

                  <div className="companyEmailCardToolbar">
                    <input
                      type="text"
                      className="companyEmailSearch"
                      value={searchTerms[config.emailType] ?? ''}
                      onChange={(event) => updateSearchTerm(config.emailType, event.target.value)}
                      placeholder={i18n.t('company.searchUsers', { defaultValue: 'Search users by name or email' })}
                      disabled={isSaving || config.users.length === 0}
                    />
                    <div className="companyEmailActions">
                      <button
                        type="button"
                        className="companyEmailActionBtn"
                        onClick={() => setAllUsersSelection(config.emailType, true)}
                        disabled={isSaving || config.users.length === 0}
                      >
                        {i18n.t('company.selectAll', { defaultValue: 'Select all' })}
                      </button>
                      <button
                        type="button"
                        className="companyEmailActionBtn"
                        onClick={() => setAllUsersSelection(config.emailType, false)}
                        disabled={isSaving || selectedUsers.length === 0}
                      >
                        {i18n.t('company.clearSelection', { defaultValue: 'Clear' })}
                      </button>
                    </div>
                  </div>

                  <div className="companyEmailSelectedWrap">
                    {selectedUsers.length === 0 ? (
                      <div className="companyEmailSelectedEmpty">
                        {i18n.t('company.noUsersSelected', { defaultValue: 'No recipients selected yet.' })}
                      </div>
                    ) : (
                      selectedUsers.map((user) => (
                        <button
                          key={`selected-${config.emailType}-${user.userId}`}
                          type="button"
                          className="companyEmailSelectedChip"
                          onClick={() => toggleEmailNotificationUser(config.emailType, user.userId)}
                          disabled={isSaving}
                        >
                          <span>{user.fullName?.trim() || user.userName || `User #${user.userId}`}</span>
                          <strong>x</strong>
                        </button>
                      ))
                    )}
                  </div>

                  <div className="companyEmailUserList">
                    {config.users.length === 0 ? (
                      <div className="companyEmailEmpty">
                        {i18n.t('company.noUsersAvailable', { defaultValue: 'No users available for this company.' })}
                      </div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="companyEmailEmpty">
                        {i18n.t('company.noMatchingUsers', { defaultValue: 'No users match this search.' })}
                      </div>
                    ) : (
                      filteredUsers.map((user) => (
                        <label
                          key={`${config.emailType}-${user.userId}`}
                          className={`companyEmailUserItem ${user.isSelected ? 'companyEmailUserItemSelected' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={user.isSelected}
                            onChange={() => toggleEmailNotificationUser(config.emailType, user.userId)}
                            disabled={isSaving}
                          />
                          <div className="companyEmailUserMeta">
                            <span className="companyEmailUserName">{user.fullName?.trim() || user.userName || `User #${user.userId}`}</span>
                            <span className="companyEmailUserEmail">{user.email || user.userName || '-'}</span>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="companyEmailPageFooter">
        <button type="button" className="companyEmailBtn companyEmailBtnPrimary" onClick={handleSave} disabled={isLoading || isSaving}>
          {isSaving ? i18n.t('company.saving', { defaultValue: 'Saving...' }) : i18n.t('generic.save', { defaultValue: 'Save' })}
        </button>
        {!isSaving && !isLoading ? (
          <span className="companyEmailFooterHint">
            {totalSelectedUsers > 0
              ? i18n.t('company.emailNotificationsSaveHint', { defaultValue: 'Save to apply your recipient changes.' })
              : i18n.t('company.emailNotificationsEmptyHint', { defaultValue: 'Select one or more users for each email type you want to notify.' })}
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default CompanyEmailNotification;
